"use client";

import React from "react";
import {
  ArrowRightLeft,
  BookPlus,
  Eye,
  Loader2,
  Minus,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import {
  TradeItem,
  TradeListType,
  type TradeListAddItemRequest,
  type TradeListAddItemResponse,
} from "modules/tradeLists/model/tradeList";
import { useCollection } from "modules/collections/details/hooks/useCollection";
import { useConfirmDeleteModal } from "modules/common/stores/useConfirmDeleteStore";
import { useCardActions } from "modules/common/components/actions";
import { revalidateTradeListCaches } from "modules/server/actions/list";
import { QUERIES } from "modules/common/query";
import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  useMultiSelectStore,
  getSelectionState,
} from "modules/common/stores/useMultiSelectStore";
import { Checkbox } from "modules/common/components/ui/Checkbox";
import { tradeListItemQueriesForLists } from "modules/tradeLists/utils/tradeListQueryFilters";
import {
  applyOptimisticInventoryTotals,
  optimisticUnitValue,
} from "modules/common/optimisticInventoryTotals";
import { invalidateInventoryFacetQueries } from "modules/common/invalidateInventoryQueries";
import type { BinderItemsRequest } from "modules/binder/model/binder";
import {
  applyOptimisticBinderTradeQuantityAdd,
  applyOptimisticBinderTradeQuantityRemove,
} from "modules/binder/optimisticAdd";
import {
  tradeItemPendingAddKey,
  useTradeItemPendingStore,
} from "modules/tradeLists/stores/useTradeItemPendingStore";
import { selectableInventoryIds } from "modules/common/selectableInventoryIds";
import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { API_ROUTES } from "utils/api-route.constants";
import { useToast } from "modules/common/components/toast/useToast";
import { extractErrorMessage } from "utils/error-handling";

const BINDER_TRADE_ITEM_MUTATION_KEY = [
  "binder",
  "trade-item-inventory",
] as const;
const BINDER_RECONCILIATION_DELAY_MS = 5_000;

interface BinderReconciliationBatch {
  listIds: Set<string>;
  timeout?: ReturnType<typeof setTimeout>;
}

const binderReconciliationBatches = new WeakMap<
  QueryClient,
  BinderReconciliationBatch
>();

function beginBinderReconciliationBatch(
  queryClient: QueryClient,
  listId: string
) {
  const batch = binderReconciliationBatches.get(queryClient) ?? {
    listIds: new Set<string>(),
  };
  if (batch.timeout) clearTimeout(batch.timeout);
  batch.timeout = undefined;
  batch.listIds.add(listId);
  binderReconciliationBatches.set(queryClient, batch);
}

function scheduleBinderReconciliation(
  queryClient: QueryClient,
  reconcile: (listIds: string[]) => Promise<void>
) {
  const batch = binderReconciliationBatches.get(queryClient);
  if (!batch) return;
  if (batch.timeout) clearTimeout(batch.timeout);

  batch.timeout = setTimeout(() => {
    if (
      queryClient.isMutating({
        mutationKey: BINDER_TRADE_ITEM_MUTATION_KEY,
        exact: true,
      }) > 0
    ) {
      scheduleBinderReconciliation(queryClient, reconcile);
      return;
    }

    binderReconciliationBatches.delete(queryClient);
    void reconcile([...batch.listIds]);
  }, BINDER_RECONCILIATION_DELAY_MS);
}

interface UseTradeItemActionsProps {
  item: TradeItem;
  binderRequest?: BinderItemsRequest;
  disabled?: boolean;
  isOwner: boolean;
  listType?: TradeListType;
  onViewCardDetails?: () => void;
  onEditItem?: () => void;
  onMoveToList?: () => void;
  moveDisabled?: boolean;
  enableSelect?: boolean;
}

export function useTradeItemActions({
  item,
  binderRequest,
  disabled = false,
  isOwner,
  listType,
  onViewCardDetails,
  onEditItem,
  onMoveToList,
  moveDisabled = false,
  enableSelect = true,
}: UseTradeItemActionsProps) {
  const { collection, addCard } = useCollection();
  const { openModal, closeModal } = useConfirmDeleteModal();
  const queryClient = useQueryClient();
  const client = useFetchWrapper();
  const toast = useToast();
  const selectableIds = selectableInventoryIds(item);
  const removableIds =
    item.list.type === TradeListType.TRADES
      ? (item.availableIds ?? [])
      : item.ids;
  const selectionState = useMultiSelectStore((s) =>
    getSelectionState(s.selectedIds, selectableIds)
  );
  const toggleItem = useMultiSelectStore((s) => s.toggleItem);
  const hasReservedCopies = (item.spokenForQuantity ?? 0) > 0;
  const pendingAddKey = tradeItemPendingAddKey(item);
  const isAddFlowPending = useTradeItemPendingStore((state) =>
    state.pendingAddKeys.has(pendingAddKey)
  );
  const beginPendingAdd = useTradeItemPendingStore((state) => state.beginAdd);
  const endPendingAdd = useTradeItemPendingStore((state) => state.endAdd);
  const hasUnresolvedCopyIds = Number(item.quantity) !== item.ids.length;
  const shouldReconcileAfterMutation = () =>
    !binderRequest ||
    queryClient.isMutating({
      mutationKey: BINDER_TRADE_ITEM_MUTATION_KEY,
      exact: true,
    }) === 0;

  const applyFallbackTotals = (totals?: {
    copies: number;
    newGroups: number;
  }) => {
    if (totals) {
      applyOptimisticInventoryTotals(
        queryClient,
        [QUERIES.TRADE_LIST_FACETS, item.list.id],
        {
          copies: totals.copies,
          value: optimisticUnitValue(item.price) * totals.copies,
          newGroups: totals.newGroups,
        }
      );
    }
  };

  const reconcileAfterMutation = async (listIds: string[]) => {
    // Expire Next's list tags before client observers refetch, otherwise a
    // fast observer can win the race and repopulate itself from stale data.
    await Promise.allSettled([revalidateTradeListCaches(listIds)]);
    await Promise.allSettled([
      queryClient.invalidateQueries({ queryKey: [QUERIES.TRADE_LISTS_PAGED] }),
      invalidateInventoryFacetQueries(queryClient, "trade", "collection"),
      queryClient.invalidateQueries(tradeListItemQueriesForLists(listIds)),
      ...listIds.map((listId) =>
        queryClient.invalidateQueries({
          queryKey: [QUERIES.TRADE_LISTS_DETAILS, listId],
        })
      ),
      queryClient.invalidateQueries({ queryKey: [QUERIES.TRADE_LISTS_USER] }),
      queryClient.invalidateQueries({ queryKey: [QUERIES.COLLECTION_CARDS] }),
      queryClient.invalidateQueries({
        queryKey: [QUERIES.CARD_OWNERSHIP_STATUS],
      }),
    ]);
  };

  const settleMutation = async (totals?: {
    copies: number;
    newGroups: number;
  }) => {
    applyFallbackTotals(totals);
    if (!binderRequest) {
      await reconcileAfterMutation([item.list.id]);
      return;
    }
    if (shouldReconcileAfterMutation()) {
      scheduleBinderReconciliation(queryClient, reconcileAfterMutation);
    }
  };

  const addOneRequest: TradeListAddItemRequest = {
    listId: item.list.id,
    dataId: item.dataId,
    finishes: item.finishes,
    language: item.language,
    condition: item.condition,
    quantity: 1,
    anyPrinting: item.anyPrinting,
  };

  // Deletes every underlying document in this grouped row (all copies of this
  // exact card + condition + language + finish + printing), not just one.
  const removeItemMutation = useMutation({
    mutationKey: binderRequest ? BINDER_TRADE_ITEM_MUTATION_KEY : undefined,
    mutationFn: () =>
      client.delete(API_ROUTES.TRADE_LISTS.DELETE_BULK_ITEMS(item.list.id), {
        ids: item.ids,
      }),
  });

  const addOneMutation = useMutation({
    mutationKey: binderRequest ? BINDER_TRADE_ITEM_MUTATION_KEY : undefined,
    mutationFn: (): Promise<TradeListAddItemResponse> =>
      client.post(API_ROUTES.TRADE_LISTS.ADD_ITEM(item.list.id), addOneRequest),
  });

  const removeOneMutation = useMutation({
    mutationKey: binderRequest ? BINDER_TRADE_ITEM_MUTATION_KEY : undefined,
    mutationFn: (itemId: string) =>
      client.delete(API_ROUTES.TRADE_LISTS.DELETE_ITEM(item.list.id)(itemId), {
        remainingIds: item.ids.filter((id) => id !== itemId),
      }),
  });

  const isRemoving = removeItemMutation.isPending;
  const isAddingOne = addOneMutation.isPending;
  const isRemovingOne = removeOneMutation.isPending;

  const handleRemoveItem = () => {
    openModal({
      name: item.name,
      loadingButton: !binderRequest,
      confirmFunc: async () => {
        if (binderRequest) {
          closeModal();
          beginBinderReconciliationBatch(queryClient, item.list.id);
        }
        const optimistic = binderRequest
          ? await applyOptimisticBinderTradeQuantityRemove(queryClient, {
              request: binderRequest,
              item,
              copies: item.ids.length,
            })
          : undefined;
        try {
          await removeItemMutation.mutateAsync();
        } catch (error) {
          optimistic?.rollback();
          toast.error(extractErrorMessage(error, "Failed to delete the card"));
          await settleMutation();
          return;
        }
        toast.success("Card deleted");
        await settleMutation(
          optimistic?.applied
            ? undefined
            : { copies: -item.ids.length, newGroups: -1 }
        );
      },
    });
  };

  const handleAddOne = async () => {
    if (useTradeItemPendingStore.getState().pendingAddKeys.has(pendingAddKey)) {
      return;
    }
    beginPendingAdd(pendingAddKey);
    if (binderRequest) {
      beginBinderReconciliationBatch(queryClient, item.list.id);
    }

    try {
      const optimistic = binderRequest
        ? await applyOptimisticBinderTradeQuantityAdd(queryClient, {
            request: binderRequest,
            item,
          })
        : undefined;
      try {
        const result = await addOneMutation.mutateAsync();
        optimistic?.commitCreatedIds(result?.ids ?? []);
      } catch (error) {
        optimistic?.rollback();
        toast.error(extractErrorMessage(error, "Failed to add the card"));
        await settleMutation();
        return;
      }
      toast.success("Card added");
      await settleMutation(
        optimistic?.applied ? undefined : { copies: 1, newGroups: 0 }
      );
    } finally {
      endPendingAdd(pendingAddKey);
    }
  };

  const handleRemoveOne = async () => {
    const itemId = removableIds[0];
    if (!itemId) return;
    if (binderRequest) {
      beginBinderReconciliationBatch(queryClient, item.list.id);
    }
    const optimistic = binderRequest
      ? await applyOptimisticBinderTradeQuantityRemove(queryClient, {
          request: binderRequest,
          item,
          copies: 1,
          removedIds: [itemId],
        })
      : undefined;
    try {
      await removeOneMutation.mutateAsync(itemId);
    } catch (error) {
      optimistic?.rollback();
      toast.error(extractErrorMessage(error, "Failed to remove the card"));
      await settleMutation();
      return;
    }
    toast.success("Card removed");
    await settleMutation(
      optimistic?.applied ? undefined : { copies: -1, newGroups: 0 }
    );
  };

  const handleAddToCollection = async () => {
    if (collection) {
      await addCard.mutateAsync({
        collectionId: collection.id,
        cardId: item.dataId,
        finishes: item.finishes,
        language: item.language,
        condition: item.condition,
        quantity: 1,
        purchasePrice: 0,
      });
    }
  };
  const handleSelectCard = () => {
    if (selectableIds.length > 0 && !hasUnresolvedCopyIds) {
      toggleItem(selectableIds);
    }
  };
  /*const handleViewInCollection = () => {
    if (collection?.id) {
      window.open(`/collections/${collection.id}`, "_blank");
    }
  };*/

  const actionDefinitions = [
    // Add to Collection action (for wants lists only)
    {
      id: "add-to-collection",
      label: "Add to Collection",
      icon: addCard.isPending ? (
        <Loader2 size={ICON_SIZE} className="animate-spin" />
      ) : (
        <BookPlus size={ICON_SIZE} />
      ),
      handler: handleAddToCollection,
      disabled: disabled || addCard.isPending || !collection,
      loading: addCard.isPending,
      variant: "success" as const,
      condition: isOwner && listType === TradeListType.WANTS,
    },
    // Catalogue details are separate from mutating the user's list group.
    {
      id: "view-details",
      label: "View Details",
      icon: <Eye size={ICON_SIZE} />,
      handler: onViewCardDetails || (() => {}),
      disabled: disabled || !onViewCardDetails,
      variant: "secondary" as const,
      condition: isOwner && !!onViewCardDetails,
    },
    // Edit only the exact mutable subset from the live availability overlay.
    // Until that subset resolves, known reservations remain conservative.
    {
      id: "edit-item",
      label: "Edit Card",
      icon: <Pencil size={ICON_SIZE} />,
      handler: onEditItem || (() => {}),
      disabled: disabled || !onEditItem,
      variant: "secondary" as const,
      condition:
        isOwner &&
        !!onEditItem &&
        selectableIds.length > 0 &&
        !hasUnresolvedCopyIds,
    },
    {
      id: "move-item",
      label: "Move to another list",
      icon: <ArrowRightLeft size={ICON_SIZE} />,
      handler: onMoveToList || (() => {}),
      disabled,
      variant: "secondary" as const,
      condition:
        isOwner && !!onMoveToList && !moveDisabled && !hasUnresolvedCopyIds,
    },
    // Add one copy of this exact card variant
    {
      id: "add-one",
      label: "Add one",
      icon:
        isAddingOne || isAddFlowPending ? (
          <Loader2 size={ICON_SIZE} className="animate-spin" />
        ) : (
          <Plus size={ICON_SIZE} />
        ),
      handler: handleAddOne,
      disabled: disabled || isAddingOne || isAddFlowPending,
      loading: isAddingOne || isAddFlowPending,
      variant: "secondary" as const,
      condition: isOwner,
    },
    // Remove one copy (the full delete handles the last/all copies)
    {
      id: "remove-one",
      label: "Remove one",
      icon: isRemovingOne ? (
        <Loader2 size={ICON_SIZE} className="animate-spin" />
      ) : (
        <Minus size={ICON_SIZE} />
      ),
      handler: handleRemoveOne,
      disabled: disabled || isRemovingOne || removableIds.length === 0,
      loading: isRemovingOne,
      variant: "secondary" as const,
      condition:
        isOwner &&
        !hasUnresolvedCopyIds &&
        item.ids.length > 1 &&
        removableIds.length > 0,
    },
    // View in Collection action (if collection exists)
    /*{
      id: "view-collection",
      label: "View Collection",
      icon: <ExternalLink size={ICON_SIZE} />,
      handler: handleViewInCollection,
      disabled: disabled || !collection,
      variant: "secondary" as const,
      condition: isOwner && !!collection,
    },*/
    // Select action
    {
      id: "select",
      label: "Select Card",
      icon: (
        <Checkbox
          checked={selectionState === "all"}
          indeterminate={selectionState === "partial"}
          size="xs"
          className="pointer-events-none p-0"
          onChange={() => {}}
          aria-label={`Select item ${item.dataId}`}
        />
      ),
      handler: handleSelectCard,
      disabled,
      variant: "secondary" as const,
      condition:
        enableSelect && selectableIds.length > 0 && !hasUnresolvedCopyIds,
    },
    // Delete item action
    {
      id: "delete",
      label: "Delete Card",
      icon: isRemoving ? (
        <Loader2 size={ICON_SIZE} className="animate-spin" />
      ) : (
        <Trash2 size={ICON_SIZE} />
      ),
      handler: handleRemoveItem,
      disabled: disabled || isRemoving,
      loading: isRemoving,
      variant: "error" as const,
      condition: isOwner && !hasReservedCopies && !hasUnresolvedCopyIds,
    },
  ];

  return useCardActions({
    actionDefinitions,
    disabled,
  });
}
